import { ReactNode, useCallback, useMemo, useState } from 'react';

export interface TabModel {
  title: string;
  isDotted?: boolean;
}

export interface TabConfig extends TabModel {
  view: ReactNode;
}

interface UseTabsProps {
  tabConfigs: TabConfig[];
  defaultTab?: number;
}

interface UseTabsReturn {
  tabs: TabModel[];
  activeTab?: number;
  activeView: ReactNode;
  onSelectTab: (index: number) => void;
}

export const useTabs = ({ tabConfigs, defaultTab }: UseTabsProps): UseTabsReturn => {
  const getValidDefaultTab = () => {
    return typeof defaultTab === 'number' ? (defaultTab < tabConfigs.length ? defaultTab : 0) : 0;
  };

  const [activeTab, setActiveTab] = useState<number | undefined>(() => {
    return tabConfigs.length ? getValidDefaultTab() : undefined;
  });

  // Handle tabConfigs changes (such as during loading) where the activeTab has not been modified by the user.
  if (typeof activeTab === 'undefined' && tabConfigs.length) {
    setActiveTab(getValidDefaultTab());
  }

  const activeView = useMemo<ReactNode>(() => {
    if (typeof activeTab !== 'undefined') {
      for (let i = 0; i < tabConfigs.length; i++) {
        if (activeTab === i) {
          return tabConfigs[i].view;
        }
      }
    }
    return null;
  }, [tabConfigs, activeTab]);

  const onSelectTab = useCallback((index: number) => {
    setActiveTab(index);
  }, []);

  const tabs: TabModel[] = tabConfigs.map(
    ({ view, ...rest }: TabConfig): TabModel => ({ ...rest }),
  );

  return {
    tabs,
    activeTab,
    activeView,
    onSelectTab,
  };
};

export default useTabs;
